import { IS_SMALL_SCREEN } from '@Constants/LeftDrawer';
import useCheckoutPresenter from '@Screens/Checkout/presenter';
import useProductsPresenter from '@Screens/Products/productSectionPresenter';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React from 'react';
import type { TextStyle, ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native';
import styled from 'styled-components/native';

import OwnableTheme from '../../../assets/OwnableTheme';
import StyledProductCard from '../StyledProductCard';
import BuyButton from './Buttons/BuyButton';
import ProductBrand from './ProductBrand';
import ProductName from './ProductName';
import ProductPrice from './ProductPrice';
import ProductPriceLabel from './ProductPriceLabel';
import ProductRrp from './ProductRrp';
import CustomSelect from './Select';

import { useProductsStore } from '~stores/products';

// noinspection JSDeprecatedSymbols
const isIPhone = navigator.platform === 'iPhone';

const StyledMain = styled.View`
  display: flex;
  flex-flow: row nowrap;
`;

const StyledProductDetailCardWrapper = styled(StyledProductCard)`
  display: flex;
  flex-direction: column;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
`;

const StyledProductDetailCardLeft = styled.View`
  flex: 1 2 auto;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const StyledPriceRow = styled.View`
  flex-direction: row;
  width: 100%;
  display: flex;
  margin-top: ${OwnableTheme.fontSizes[IS_SMALL_SCREEN ? 'x4s' : 'xxs']}px;
`;

const StyledOptionsRow = styled.View`
  margin-top: ${IS_SMALL_SCREEN ? 15 : 18}px;
`;

const StyledActionsRow = styled.View`
  flex-direction: row;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  row-gap: ${IS_SMALL_SCREEN ? 13 : 20}px;
  column-gap: ${IS_SMALL_SCREEN ? 5 : 9}px;
  margin-top: ${IS_SMALL_SCREEN ? 18 : 25}px;
  margin-bottom: ${isIPhone ? '30px' : '0px'};
`;

const buttonLabelStyle: TextStyle = {
  fontFamily: OwnableTheme.fonts.gilroy.regular,
  fontWeight: '600',
  fontSize: IS_SMALL_SCREEN ? 10 : 15,
  letterSpacing: IS_SMALL_SCREEN ? 0.3 : 0.45,
};

const ProductDetailCard: FC = () => {
  const { currentProduct, currentProductSelectedOption } = useProductsStore();
  const { currentProductPriceForOption } = useProductsPresenter();
  const { selectProductOption } = useCheckoutPresenter();

  const getPadding = (index: number) => {
    if (IS_SMALL_SCREEN) {
      if (index === 0) {
        return 26;
      }
      return 4;
    }
    if (index === 0) {
      return 64;
    }
    if (index === 1) {
      return 34;
    }
    return 14;
  };

  const contentStyle: (index: number) => ViewStyle = (index) => ({
    height: IS_SMALL_SCREEN ? 32 : 46,
    paddingHorizontal: getPadding(index),
    borderWidth: 1,
    borderRadius: 100,
    margin: 0,
  });
  const fontOverride = { fontSize: IS_SMALL_SCREEN ? 15 : 22 };
  if (!currentProduct) {
    return null;
  }

  const showOptions =
    currentProduct?.options?.length > 0 &&
    !!currentProduct?.actions?.find(
      (action) => action.type?.toLowerCase() === 'buy'
    );

  return (
    <StyledProductDetailCardWrapper>
      <StyledMain>
        <StyledProductDetailCardLeft>
          <ProductBrand
            weight='bold'
            brandName={currentProduct.brand}
            color={OwnableTheme.colors.black}
            size={IS_SMALL_SCREEN ? 'xxs' : 'ml'}
            fontStyleOverride={fontOverride}
          />
          <ProductName
            size={IS_SMALL_SCREEN ? 'xxs' : 'ml'}
            weight='regular'
            productName={currentProduct.name}
            color={OwnableTheme.colors.black}
            fontStyleOverride={fontOverride}
          />
        </StyledProductDetailCardLeft>
      </StyledMain>

      <StyledPriceRow>
        <ProductPriceLabel
          size={IS_SMALL_SCREEN ? 'xxs' : 'ml'}
          priceLabel={currentProduct.priceLabel}
          color={OwnableTheme.colors.black}
        />
        <ProductPrice
          size={IS_SMALL_SCREEN ? 'xxs' : 'ml'}
          weight='bold'
          price={currentProductPriceForOption}
          color={OwnableTheme.colors.black}
        />
        <ProductRrp
          size={IS_SMALL_SCREEN ? 'x4s' : 'xxxs'}
          rrp={currentProductSelectedOption?.rrp ?? currentProduct.rrp}
          color={OwnableTheme.colors.darkGray}
        />
      </StyledPriceRow>

      {showOptions && (
        <StyledOptionsRow>
          <CustomSelect
            placeholder={`< Choose ${currentProduct.optionsLabel ?? ''} >`}
            options={currentProduct.options.map((item) => ({
              label: item.optionName,
              value: item.id,
            }))}
            onChange={(value) => {
              selectProductOption(
                currentProduct.options?.find((o) => o.id === value?.value)
              );
            }}
          />
        </StyledOptionsRow>
      )}

      <StyledActionsRow>
        {currentProduct.actions.length > 0 &&
          currentProduct.actions.map((item, i) => (
            <BuyButton
              title={item.displayName}
              method={item.type.toLocaleLowerCase()}
              methodId={item.id}
              supplierVisitLink={item.url}
              labelStyle={{ ...buttonLabelStyle }}
              buttonStyle={styles.buyButton}
              contentStyle={contentStyle(i)}
              color={
                i === 0 ? OwnableTheme.colors.black : OwnableTheme.colors.white
              }
            />
          ))}
      </StyledActionsRow>
    </StyledProductDetailCardWrapper>
  );
};

export default observer(ProductDetailCard);

const styles = StyleSheet.create({
  buyButton: { borderRadius: 100 },
});
